/**
 * Modelo de etapas do agendamento público.
 *
 * Mantido puro de propósito: a página `/[slug]` é grande demais para testar o
 * avanço de etapas via render, então a regra de "pode avançar / até onde dá para
 * ir" vive aqui e é coberta por `bookingWizard.test.ts`.
 */

export type BookingWizardStepId =
  | "service"
  | "provider"
  | "schedule"
  | "showcase"
  | "account"
  | "details";

export interface BookingWizardContext {
  hasSelectedService: boolean;
  allowAnyProvider: boolean;
  /** Falso quando o estabelecimento não cadastrou profissionais públicos. */
  hasProviderOptions: boolean;
  hasSelectedProvider: boolean;
  hasSelectedDate: boolean;
  hasSelectedTime: boolean;
  hasContactData: boolean;
  hasShowcaseProducts: boolean;
  isCustomerLoggedIn: boolean;
}

export interface BookingWizardStep {
  id: BookingWizardStepId;
  /** Rótulo curto usado na trilha de progresso. */
  label: string;
  /** Título animado exibido no topo da etapa. */
  title: string;
  subtitle: string;
  /** Etapas opcionais podem ser puladas sem preencher nada. */
  optional: boolean;
}

const STEP_DEFINITIONS: Record<BookingWizardStepId, Omit<BookingWizardStep, "id">> = {
  provider: {
    label: "Profissional",
    title: "Quem vai te atender?",
    subtitle:
      "Escolha um profissional (ou deixe que a gente indica quem tiver horário). Os serviços da próxima etapa dependem dessa escolha.",
    optional: false,
  },
  service: {
    label: "Serviço",
    title: "O que você quer agendar?",
    subtitle: "Estes são os serviços que esse profissional realiza.",
    optional: false,
  },
  schedule: {
    label: "Dia e horário",
    title: "Escolha o dia e o horário",
    subtitle: "Selecione uma data com agenda aberta e depois o horário.",
    optional: false,
  },
  showcase: {
    label: "Vitrine",
    title: "Quer levar algo junto?",
    subtitle: "Dê uma olhada nos produtos do estabelecimento. Esta etapa é opcional.",
    optional: true,
  },
  account: {
    label: "Conta",
    title: "Quer criar sua conta?",
    subtitle: "Com conta, seus dados já vêm preenchidos nos próximos agendamentos.",
    optional: true,
  },
  details: {
    label: "Dados",
    title: "Falta pouco: seus dados",
    subtitle: "Confirme os dados de contato para fechar o agendamento.",
    optional: false,
  },
};

function createStep(id: BookingWizardStepId): BookingWizardStep {
  return { id, ...STEP_DEFINITIONS[id] };
}

/**
 * Monta a lista de etapas visíveis. Vitrine só entra quando o estabelecimento
 * tem produtos públicos; conta só entra para quem ainda não está logado.
 */
export function buildBookingWizardSteps(
  context: BookingWizardContext
): BookingWizardStep[] {
  const steps: BookingWizardStep[] = [];

  // Profissional vem antes do serviço: nem todo profissional faz todo
  // serviço, então a escolha do profissional filtra o que aparece a seguir.
  // Sem profissionais cadastrados não há o que escolher: a etapa sairia vazia.
  if (context.hasProviderOptions) steps.push(createStep("provider"));

  steps.push(createStep("service"), createStep("schedule"));

  if (context.hasShowcaseProducts) steps.push(createStep("showcase"));
  if (!context.isCustomerLoggedIn) steps.push(createStep("account"));

  steps.push(createStep("details"));
  return steps;
}

/**
 * Uma etapa está completa quando o cliente já pode seguir adiante a partir dela.
 * Etapas opcionais estão sempre completas.
 */
export function isBookingStepComplete(
  stepId: BookingWizardStepId,
  context: BookingWizardContext
): boolean {
  switch (stepId) {
    case "service":
      return context.hasSelectedService;
    case "provider":
      if (!context.hasProviderOptions) return true;
      return context.allowAnyProvider ? true : context.hasSelectedProvider;
    case "schedule":
      return context.hasSelectedDate && context.hasSelectedTime;
    case "showcase":
    case "account":
      return true;
    case "details":
      return context.hasContactData;
    default:
      return false;
  }
}

/**
 * Mensagem curta para explicar por que o botão "Próximo" está desabilitado.
 */
export function getBookingStepBlockedReason(
  stepId: BookingWizardStepId,
  context: BookingWizardContext
): string | null {
  if (isBookingStepComplete(stepId, context)) return null;

  switch (stepId) {
    case "service":
      return "Escolha um serviço para continuar.";
    case "provider":
      return "Escolha um profissional para continuar.";
    case "schedule":
      return context.hasSelectedDate
        ? "Escolha um horário para continuar."
        : "Escolha uma data para continuar.";
    case "details":
      return "Preencha seus dados de contato para continuar.";
    default:
      return null;
  }
}

/**
 * Última etapa alcançável: a primeira incompleta. Impede que o cliente volte,
 * mude o serviço e pule direto para o formulário com estado inválido.
 */
export function getMaxReachableStepIndex(
  steps: BookingWizardStep[],
  context: BookingWizardContext
): number {
  for (let index = 0; index < steps.length; index += 1) {
    if (!isBookingStepComplete(steps[index].id, context)) return index;
  }
  return Math.max(steps.length - 1, 0);
}

export function findBookingStepIndex(
  steps: BookingWizardStep[],
  stepId: BookingWizardStepId | null | undefined
): number {
  if (!stepId) return -1;
  return steps.findIndex((step) => step.id === stepId);
}

/**
 * Ajusta um índice pedido para dentro dos limites válidos.
 */
export function clampBookingStepIndex(
  requestedIndex: number,
  steps: BookingWizardStep[],
  context: BookingWizardContext
): number {
  if (steps.length === 0) return 0;
  const maxReachable = getMaxReachableStepIndex(steps, context);
  const upperBound = Math.min(maxReachable, steps.length - 1);
  if (!Number.isFinite(requestedIndex) || requestedIndex < 0) return 0;
  return Math.min(Math.trunc(requestedIndex), upperBound);
}

/**
 * Quantas etapas obrigatórias ainda faltam — usado no rótulo de progresso.
 */
export function getBookingWizardProgress(
  steps: BookingWizardStep[],
  currentIndex: number
): { current: number; total: number; percent: number } {
  const total = Math.max(steps.length, 1);
  const current = Math.min(Math.max(currentIndex + 1, 1), total);
  return {
    current,
    total,
    percent: Math.round((current / total) * 100),
  };
}
